import { useState, useRef, useEffect } from '@wordpress/element';

/* ── TextInput ── */
export const TextInput = ({ value = '', onChange, type = 'text', placeholder = '', disabled = false }) => (
    <input
        className="dss-input"
        type={type}
        value={value || ''}
        placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
    />
);

/* ── Toggle (replaces CheckboxControl with 'on'/'no' values) ── */
export const Toggle = ({ checked, onChange }) => (
    <label className="dss-toggle">
        <span
            className={`dss-toggle__track${checked ? ' dss-toggle__track--on' : ''}`}
            role="switch"
            aria-checked={checked}
            tabIndex={0}
            onClick={() => onChange(!checked)}
            onKeyDown={e => (e.key === ' ' || e.key === 'Enter') && onChange(!checked)}
        >
            <span className="dss-toggle__thumb" />
        </span>
        <span className={`dss-toggle__label${checked ? ' dss-toggle__label--on' : ''}`}>
            {checked ? 'Enabled' : 'Disabled'}
        </span>
    </label>
);

/* ── Select ── */
export const Select = ({ value, options = [], onChange, disabled = false }) => (
    <select
        className="dss-select"
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
    >
        {options.map(opt => (
            <option key={opt.value ?? opt.key} value={opt.value ?? opt.key}>
                {opt.label ?? opt.name}
            </option>
        ))}
    </select>
);

/* ── ColorPicker ── */
export const ColorPicker = ({ value = '#000000', onChange }) => {
    const [hex, setHex]   = useState(value || '#000000');
    const [open, setOpen] = useState(false);
    const wrapRef         = useRef(null);

    useEffect(() => { setHex(value || '#000000'); }, [value]);

    useEffect(() => {
        if (!open) return;
        const h = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, [open]);

    const PRESETS = [
        '#000000','#ffffff','#ef4444','#f97316','#eab308',
        '#22c55e','#3b82f6','#8b5cf6','#ec4899','#0ea5e9',
        '#10b981','#14b8a6','#f59e0b','#64748b','#94a3b8',
    ];

    return (
        <div className="dss-color-picker" ref={wrapRef}>
            <button type="button" className="dss-color-trigger" onClick={() => setOpen(o => !o)}>
                <span className="dss-color-trigger__swatch" style={{ background: hex }} />
                <span className="dss-color-trigger__hex">{hex}</span>
                <span className="dss-color-trigger__arrow">{open ? '▲' : '▼'}</span>
            </button>
            {open && (
                <div className="dss-color-dropdown">
                    <div className="dss-color-wheel-wrap">
                        <input type="color" className="dss-color-wheel" value={hex}
                            onChange={e => { setHex(e.target.value); onChange(e.target.value); }} />
                        <span className="dss-color-wheel-hint">Click to open colour wheel</span>
                    </div>
                    <div className="dss-color-presets">
                        {PRESETS.map(c => (
                            <button key={c} type="button"
                                className={`dss-color-preset${hex === c ? ' dss-color-preset--active' : ''}`}
                                style={{ background: c }}
                                onClick={() => { setHex(c); onChange(c); }}
                                title={c}
                            />
                        ))}
                    </div>
                    <div className="dss-color-hex-row">
                        <span className="dss-color-hex-label">HEX</span>
                        <input className="dss-color-hex-input" type="text" value={hex} maxLength={7}
                            onChange={e => {
                                const v = e.target.value; setHex(v);
                                if (/^#[0-9a-fA-F]{6}$/.test(v)) onChange(v);
                            }} />
                    </div>
                </div>
            )}
        </div>
    );
};
